/** The small still that represents a project in the file list. */
export const POSTER_MAX_PX = 384;

/**
 * Drawn once at import rather than derived from the original each time the list opens:
 * decoding a 40-megapixel photo to fill a 96px tile is work the list should never do.
 */
export async function renderPoster(bitmap: ImageBitmap): Promise<Blob | null> {
  const scale = Math.min(1, POSTER_MAX_PX / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  try {
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, width, height);
    return await canvas.convertToBlob({ type: "image/webp", quality: 0.8 });
  } catch {
    // A poster is a convenience; failing to make one must not fail the import.
    return null;
  }
}

/** Wide enough to tell two recordings apart, small enough to keep in a list. */
const POSTER_WIDTH = 384;
const POSTER_HEIGHT = 216;
/** Reading every sample of an hour-long file to draw 384 columns is wasted work. */
const SAMPLES_PER_COLUMN = 512;
/*
 * The tile has no theme of its own, so the picture carries its own ground. Left
 * transparent it showed the list's alpha checkerboard, which reads as "this file has
 * transparency" rather than "this is a recording".
 */
const WAVE_PAPER = "#e9edf3";
const WAVE_INK = "#5c6b85";
const WAVE_AXIS = "#c3cbd8";

/**
 * A squashed waveform, which is all a list needs to tell one recording from another.
 *
 * Drawn from the decoded channels at import, before they are handed to the worker: the
 * data is already in memory there, and reading it back out of OPFS later would cost more
 * than the picture is worth.
 */
export async function renderWaveformPoster(
  channels: readonly Float32Array[],
  frameCount: number,
): Promise<Blob | null> {
  const first = channels[0];
  if (!first || frameCount <= 0) return null;

  try {
    const canvas = new OffscreenCanvas(POSTER_WIDTH, POSTER_HEIGHT);
    const context = canvas.getContext("2d");
    if (!context) return null;

    const middle = POSTER_HEIGHT / 2;
    const framesPerColumn = frameCount / POSTER_WIDTH;
    const stride = Math.max(1, Math.floor(framesPerColumn / SAMPLES_PER_COLUMN));

    context.fillStyle = WAVE_PAPER;
    context.fillRect(0, 0, POSTER_WIDTH, POSTER_HEIGHT);
    context.fillStyle = WAVE_AXIS;
    context.fillRect(0, middle - 0.5, POSTER_WIDTH, 1);

    context.fillStyle = WAVE_INK;
    for (let column = 0; column < POSTER_WIDTH; column += 1) {
      const from = Math.floor(column * framesPerColumn);
      const to = Math.min(Math.floor((column + 1) * framesPerColumn), frameCount);
      let peak = 0;
      for (const channel of channels) {
        for (let frame = from; frame < to; frame += stride) {
          const size = Math.abs(channel[frame] ?? 0);
          if (size > peak) peak = size;
        }
      }
      // At least a hairline, so silence still reads as a track rather than a blank tile.
      const half = Math.max(0.5, peak * middle);
      context.fillRect(column, middle - half, 1, half * 2);
    }
    return await canvas.convertToBlob({ type: "image/webp", quality: 0.8 });
  } catch {
    return null;
  }
}

/** How far in to look for a frame worth showing: the very first one is often black. */
const VIDEO_POSTER_AT = 0.1;
const VIDEO_POSTER_MAX_SEC = 2;
const VIDEO_POSTER_TIMEOUT_MS = 8_000;

/**
 * A frame from near the start of the clip. Like every poster this is a convenience, so
 * anything that goes wrong — an undecodable codec, a tab that was in the background —
 * gives back nothing rather than failing the import.
 */
export async function renderVideoPoster(file: File): Promise<Blob | null> {
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.preload = "auto";
  video.muted = true;
  video.playsInline = true;

  try {
    const frame = await new Promise<ImageBitmap | null>((resolve) => {
      const timer = setTimeout(() => resolve(null), VIDEO_POSTER_TIMEOUT_MS);
      const give = (bitmap: ImageBitmap | null): void => {
        clearTimeout(timer);
        resolve(bitmap);
      };

      video.addEventListener("error", () => give(null), { once: true });
      video.addEventListener(
        "loadeddata",
        () => {
          const at = Math.min(video.duration * VIDEO_POSTER_AT, VIDEO_POSTER_MAX_SEC);
          video.addEventListener(
            "seeked",
            () => void createImageBitmap(video).then(give, () => give(null)),
            { once: true },
          );
          video.currentTime = Number.isFinite(at) ? at : 0;
        },
        { once: true },
      );
      video.src = url;
    });

    if (!frame) return null;
    try {
      return await renderPoster(frame);
    } finally {
      frame.close();
    }
  } catch {
    return null;
  } finally {
    video.removeAttribute("src");
    URL.revokeObjectURL(url);
  }
}
